import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { formatCurrency } from '../booking.utils';

// ============================================================================
// TYPES
// ============================================================================

export type DiscountType = 'Percentage' | 'FlatAmount';

/** Form-level discount state. Held alongside additionalData on BookingFormModal. */
export interface DiscountData {
  discountType: DiscountType | '';
  discountValue: number;
  discountRemarks: string;
}

interface DiscountSectionProps {
  data: DiscountData;
  onChange: (updates: Partial<DiscountData>) => void;
  fieldErrors: Record<string, string>;
  expanded: boolean;
  onToggle: () => void;
  /** Subtotal before discount, used to live-preview the net amount. */
  subtotal: number;
  /** True when the booking is marked complimentary — locks the inputs. */
  isComplimentary: boolean;
  /** Cashier-entered comp reason (mandatory when isComplimentary). */
  complimentaryReason: string;
  /** Toggle handler for comp flag. */
  onIsComplimentaryChange: (value: boolean) => void;
  /** Setter for comp reason. */
  onComplimentaryReasonChange: (value: string) => void;
  /** True when the live computation says approval will be required (drives the red pill). */
  requiresApproval: boolean;
  /** Reasons string built by the form (e.g. "Discount 25% > 20% threshold"). Tooltip on the pill. */
  approvalReasons?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

const TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'No discount' },
  { value: 'Percentage', label: 'Percentage (%)' },
  { value: 'FlatAmount', label: 'Flat amount (₹)' },
];

const DiscountSection: React.FC<DiscountSectionProps> = ({
  data, onChange, fieldErrors, expanded, onToggle,
  subtotal, isComplimentary, complimentaryReason,
  onIsComplimentaryChange, onComplimentaryReasonChange,
  requiresApproval, approvalReasons,
}) => {
  // Effective discount + net for the inline preview.
  // For complimentary, the discount equals the full subtotal — server enforces this
  // too but we mirror it here so the UI is honest.
  const { computedDiscount, netAmount } = useMemo(() => {
    if (isComplimentary) {
      return { computedDiscount: subtotal, netAmount: 0 };
    }
    if (!data.discountType || !data.discountValue || data.discountValue <= 0 || subtotal <= 0) {
      return { computedDiscount: 0, netAmount: subtotal };
    }
    const raw = data.discountType === 'Percentage'
      ? subtotal * (data.discountValue / 100)
      : data.discountValue;
    const clamped = Math.min(Math.max(0, raw), subtotal);
    return { computedDiscount: clamped, netAmount: subtotal - clamped };
  }, [isComplimentary, data.discountType, data.discountValue, subtotal]);

  const hasDiscount = computedDiscount > 0;
  const inputsLocked = isComplimentary;

  return (
    <div>
      <div
        className={`collapsible-section-header${expanded ? ' expanded' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <IconSystem.chevronRight size={14} className="chevron" />
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Discount &amp; Complimentary
        </span>
        {hasDiscount && (
          <span style={{
            fontSize: '0.7rem',
            padding: '0.125rem 0.4rem',
            borderRadius: '0.25rem',
            background: '#fef3c7',
            color: '#92400e',
            fontWeight: 600,
            marginLeft: '0.5rem',
          }}>
            -{formatCurrency(computedDiscount)}
          </span>
        )}
        {requiresApproval && (
          <span
            style={{
              fontSize: '0.7rem',
              padding: '0.125rem 0.4rem',
              borderRadius: '0.25rem',
              background: '#fee2e2',
              color: '#991b1b',
              fontWeight: 600,
              marginLeft: '0.5rem',
            }}
            title={approvalReasons ?? 'Booking will require approval'}
          >
            Approval required
          </span>
        )}
      </div>

      <div className={`collapsible-section-body${expanded ? ' expanded' : ''}`}>
        {/* Comp toggle lives next to discount because it's a 100% discount in
            disguise — net total drops to 0 and the same approval rules apply. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isComplimentary}
              onChange={e => onIsComplimentaryChange(e.target.checked)}
            />
            Complimentary Stay
          </label>
        </div>

        {isComplimentary && (
          <div style={{ marginBottom: '0.75rem' }}>
            <label className="form-label">Complimentary Reason <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${fieldErrors['complimentaryReason'] ? ' form-field-error' : ''}`}
              value={complimentaryReason}
              onChange={e => onComplimentaryReasonChange(e.target.value)}
              placeholder="Reason for complimentary stay"
              maxLength={200}
            />
            {fieldErrors['complimentaryReason'] && <div className="form-error-message">{fieldErrors['complimentaryReason']}</div>}
          </div>
        )}

        {inputsLocked && (
          <div style={{
            fontSize: '0.75rem',
            padding: '0.5rem 0.75rem',
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            borderRadius: '0.25rem',
            background: 'var(--bg-secondary, #f8fafc)',
            color: 'var(--text-secondary)',
          }}>
            Complimentary booking — Net Total auto-set to ₹0. Original cost retained for audit; discount inputs are disabled.
          </div>
        )}

        <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
          <div>
            <SyutSelect
              label="Discount Type"
              value={data.discountType || ''}
              options={TYPE_OPTIONS}
              onChange={(v) => onChange({ discountType: ((v as string) || '') as DiscountType | '' })}
              isClearable={false}
              isDisabled={inputsLocked}
              size="sm"
            />
          </div>
          <div>
            <label className="form-label">
              Discount Value
              {data.discountType === 'Percentage' && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (%)</span>}
              {data.discountType === 'FlatAmount' && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (₹)</span>}
            </label>
            <input
              type="number"
              className={`form-input${fieldErrors['discountValue'] ? ' form-field-error' : ''}`}
              value={data.discountValue || ''}
              onChange={e => onChange({ discountValue: parseFloat(e.target.value) || 0 })}
              min={0}
              max={data.discountType === 'Percentage' ? 100 : undefined}
              step={data.discountType === 'Percentage' ? 0.5 : 0.01}
              placeholder={data.discountType === 'Percentage' ? '0' : '0.00'}
              disabled={inputsLocked || !data.discountType}
            />
            {fieldErrors['discountValue'] && <div className="form-error-message">{fieldErrors['discountValue']}</div>}
          </div>
        </div>

        <div style={{ marginTop: '0.75rem' }}>
          <label className="form-label">
            Remarks
            {!inputsLocked && hasDiscount && <span className="text-danger"> *</span>}
          </label>
          <textarea
            className={`form-textarea${fieldErrors['discountRemarks'] ? ' form-field-error' : ''}`}
            value={data.discountRemarks}
            onChange={e => onChange({ discountRemarks: e.target.value })}
            rows={2}
            maxLength={500}
            placeholder={inputsLocked ? 'Use Complimentary Reason above' : 'Reason for the discount (audit / approval)'}
            disabled={inputsLocked}
            style={{ resize: 'vertical', minHeight: '50px' }}
          />
          {fieldErrors['discountRemarks'] && <div className="form-error-message">{fieldErrors['discountRemarks']}</div>}
        </div>

        {/* Inline preview — kept brief; the booking summary panel shows the full breakdown. */}
        {(hasDiscount || subtotal > 0) && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem 0.75rem',
            borderRadius: '0.25rem',
            background: 'var(--bg-secondary, #f8fafc)',
            fontSize: '0.75rem',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {hasDiscount && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b45309' }}>
                <span>{isComplimentary ? 'Complimentary' : 'Discount'}</span>
                <span>-{formatCurrency(computedDiscount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: '0.25rem' }}>
              <span>Net Total</span>
              <span>{formatCurrency(netAmount)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountSection;
